<template>
  <div class="mb-4">
    <div class="grid grid-cols-3 gap-x-3">
      <FieldWithLabel
        class="col-span-2 !mb-0"
        :id="$attrs.id"
        :label="$attrs.label"
        :type="$attrs.type"
        :options="$attrs.options"
        @input="$emit('input', $event)"
        :disabled="disabled"
        :placeholder="$attrs.placeholder"
      />

      <div>
        <button
          :class="[{ 'mt-5': $attrs.label }, 'btn h-[42px] w-full px-3 text-sm !mb-0 transition-all']"
          type="button"
          :disabled="isButtonDisabled"
          @click="validation"
        >
          <i v-if="submitting || waitingForTurnstile" class="el-icon-loading mr-1"></i>
          {{ text }}
        </button>
      </div>
    </div>
    
    <div v-show="turnstileSiteKey && !turnstileToken" class="mt-3 overflow-hidden rounded-md transition-all">
      <div ref="turnstile"></div>
      <p class="mt-2 text-xs" :class="turnstileMessageClass">
        <i v-if="turnstileMessageIcon" :class="turnstileMessageIcon" class="mr-1"></i>
        {{ turnstileMessage }}
      </p>
    </div>

    <p class="mt-2 text-xs leading-5 text-gray-400">
      若收不到短訊，請聯絡客服協助。
    </p>
  </div>
</template>

<script>
export default {
  head() {
    const siteKey = process.env.CLOUDFLARE_TURNSTILE_SITE_KEY;

    if (!siteKey) return {};

    return {
      script: [
        {
          hid: "cloudflare-turnstile",
          src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
          async: true,
          defer: true,
        },
      ],
    };
  },
  props: {
    authtype: {
      type: String,
      default: "register",
    },
    tel: {
      type: [String, Number],
      required: true,
    },
    code: {
      type: String,
      default: "+853",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      count: 0,
      countdown: null,
      submitting: false,
      turnstileWidgetId: null,
      turnstileToken: "",
      waitingForTurnstile: false,
      turnstileMessage: "需要人機驗證後才能索取認證碼。",
    };
  },

  mounted() {
    this.renderTurnstile();
  },

  beforeDestroy() {
    if (this.countdown) clearInterval(this.countdown);
    this.removeTurnstile();
  },

  methods: {
    counting(seconds = 180) {
      this.count = seconds;
      this.countdown = setInterval(() => {
        this.count -= 1;

        if (this.count <= 0) clearInterval(this.countdown);
      }, 1000);
    },
    async validation() {
      if (this.count || this.submitting) return;

      const phoneError = this.validatePhone();
      if (phoneError) {
        this.$loader.error(phoneError);
        return;
      }

      this.$loader.load();
      try {
        this.submitting = true;
        const turnstileToken = await this.getTurnstileToken();
        const { data } = await this.$axios.post("/api/verify/tel", {
          type: this.authtype,
          tel: this.tel,
          country_code: this.code,
          turnstile_token: turnstileToken,
        });

        this.counting(data?.retry_after || 180);
      } catch (e) {
        this.handleVerifyError(e);
      } finally {
        this.resetTurnstileToken();
        this.submitting = false;
        this.$loader.close();
      }
    },
    validatePhone() {
      const tel = String(this.tel || "").trim();
      const code = String(this.code || "").trim();

      if (!tel) return "請先輸入聯絡電話";

      const rules = {
        "+853": /^\d{8}$/,
        "+852": /^\d{8}$/,
        "+86": /^\d{11}$/,
        "+886": /^\d{10}$/,
      };

      if (!rules[code]) return "電話區號不正確";
      if (!rules[code].test(tel)) return "手機號格式不正確";

      return "";
    },
    getTurnstileToken() {
      if (!this.turnstileSiteKey) return Promise.resolve(null);
      if (this.turnstileToken) return Promise.resolve(this.turnstileToken);

      this.waitingForTurnstile = true;
      this.turnstileMessage = "人機驗證處理中，請稍候...";

      return this.renderTurnstile().then(() => {
        return this.waitForTurnstileToken()
          .finally(() => {
            this.waitingForTurnstile = false;
          });
      });
    },
    renderTurnstile() {
      if (!this.turnstileSiteKey) return Promise.resolve();
      if (this.turnstileWidgetId !== null) return Promise.resolve();
      if (!this.$refs.turnstile) return Promise.resolve();

      return this.waitForTurnstile().then(() => {
        if (this.turnstileWidgetId !== null) return;

        this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
          sitekey: this.turnstileSiteKey,
          action: "send_sms_otp",
          callback: (token) => {
            this.turnstileToken = token;
            this.waitingForTurnstile = false;
            this.turnstileMessage = "人機驗證完成，可索取認證碼。";
          },
          "error-callback": () => {
            this.handleTurnstileReset("人機驗證失敗，請重新嘗試。");
          },
          "expired-callback": () => {
            this.handleTurnstileReset("人機驗證已過期，請重新嘗試。");
          },
        });
      });
    },
    waitForTurnstile(retries = 20) {
      if (window.turnstile && window.turnstile.render) return Promise.resolve();
      if (retries <= 0) {
        return Promise.reject(new Error("人機驗證載入失敗，請刷新頁面後再試"));
      }

      return new Promise((resolve) => {
        setTimeout(resolve, 250);
      }).then(() => {
        return this.waitForTurnstile(retries - 1);
      });
    },
    waitForTurnstileToken(retries = 40) {
      if (this.turnstileToken) return Promise.resolve(this.turnstileToken);
      if (retries <= 0) {
        return Promise.reject(new Error("人機驗證未完成，請稍後再試"));
      }

      return new Promise((resolve) => {
        setTimeout(resolve, 250);
      }).then(() => {
        return this.waitForTurnstileToken(retries - 1);
      });
    },
    resetTurnstileToken() {
      this.turnstileToken = "";
      this.waitingForTurnstile = false;
      this.turnstileMessage = "需要重新驗證後才能索取認證碼。";

      if (this.turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    },
    handleTurnstileReset(message) {
      this.turnstileToken = "";
      this.waitingForTurnstile = false;
      this.turnstileMessage = message;
    },
    removeTurnstile() {
      if (this.turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.remove(this.turnstileWidgetId);
        this.turnstileWidgetId = null;
      }

      this.turnstileToken = "";
      this.waitingForTurnstile = false;
    },
    handleVerifyError(error) {
      const errors = error?.response?.data?.errors;
      const firstKey = errors ? Object.keys(errors)[0] : null;
      const message = firstKey && errors[firstKey]?.length
        ? errors[firstKey][0]
        : error?.message
        ? error.message
        : "發送驗證碼失敗，請稍後再試";

      this.$loader.error(message);
    },
  },
  computed: {
    turnstileSiteKey() {
      return process.env.CLOUDFLARE_TURNSTILE_SITE_KEY || "";
    },
    isButtonDisabled() {
      return this.disabled || this.count > 0 || this.submitting || this.waitingForTurnstile;
    },
    turnstileMessageClass() {
      if (this.turnstileMessage.includes("失敗") || this.turnstileMessage.includes("過期")) return "text-red-500 font-medium";
      if (this.turnstileMessage.includes("完成")) return "text-green-500 font-medium";
      return "text-chicago";
    },
    turnstileMessageIcon() {
      if (this.turnstileMessage.includes("失敗") || this.turnstileMessage.includes("過期")) return "el-icon-error";
      if (this.turnstileMessage.includes("完成")) return "el-icon-success";
      if (this.waitingForTurnstile) return "el-icon-loading";
      return "el-icon-info";
    },
    text() {
      if (this.waitingForTurnstile) return "驗證中...";
      if (this.submitting) return "發送中...";
      return this.count ? `${this.count} 秒後重試` : "獲取認證碼";
    },
  },
};
</script>

<style lang="scss" scoped></style>
